// Autometrics dashboard v3 - shared state. Data and filters live at module
// level so every page (each its own sidebar route) sees the same selection,
// and moving between pages never refetches.

import { useSyncExternalStore } from 'react';
import moment from 'moment';
import redirectURL from '../redirectURL';
import { SEVERITY_DEFAULTS } from './amConfig';
import {
  prepareRows, monthLabel, POD_BUCKETS, dayOf, applyCo2Settings, CO2_DEFAULTS,
} from './amRules';

// CO2 calculation settings, saved per browser (as the prototype does).
const CO2_CFG_KEY = 'am_co2_settings_v1';
const cloneCfg = (c) => JSON.parse(JSON.stringify(c));
function loadCo2Cfg() {
  const cfg = cloneCfg(CO2_DEFAULTS);
  try {
    const saved = JSON.parse(window.localStorage.getItem(CO2_CFG_KEY) || 'null');
    if (saved && typeof saved === 'object') {
      Object.keys(cfg.ef).forEach((k) => { if (Number.isFinite(saved.ef && saved.ef[k])) cfg.ef[k] = saved.ef[k]; });
      ['roadFactor', 'minKm'].forEach((k) => { if (Number.isFinite(saved[k])) cfg[k] = saved[k]; });
      Object.keys(cfg.base).forEach((g) => ['tco2PerTon', 'grossT'].forEach((k) => {
        const v = saved.base && saved.base[g] && saved.base[g][k];
        if (Number.isFinite(v)) cfg.base[g][k] = v;
      }));
    }
  } catch (e) { /* storage unavailable - use reference values */ }
  return cfg;
}
function saveCo2Cfg(cfg) {
  try { window.localStorage.setItem(CO2_CFG_KEY, JSON.stringify(cfg)); } catch (e) { /* ignore */ }
}

export const AM_ROUTES = {
  executive: '/autometrics/executive',
  network: '/autometrics/network',
  ftl: '/autometrics/ftl-utilization',
  pod: '/autometrics/pod-compliance',
  co2: '/autometrics/co2',
  explorer: '/autometrics/shipments',
};

// Global filters (spec section 7). `field` is the prepared-row property.
const ADH_ORDER = ['Early', 'On date', 'Late'];

export const FILTER_GROUPS = ['Period', 'Business', 'Geography & lane', 'Performance & POD'];

export const FILTER_DEFS = [
  { key: 'year', label: 'Year', field: 'year', group: 'Period' },
  { key: 'quarter', label: 'Quarter', field: 'quarter', group: 'Period' },
  { key: 'month', label: 'Month', field: 'month', group: 'Period', format: monthLabel },
  { key: 'entity', label: 'Entity', field: 'entity', group: 'Business' },
  { key: 'type', label: 'Shipment type', field: 'type', group: 'Business' },
  { key: 'primSec', label: 'Primary / secondary', field: 'primSec', group: 'Business' },
  { key: 'movement', label: 'Movement type', field: 'movement', group: 'Business' },
  { key: 'invoiceType', label: 'Invoice type', field: 'invoiceType', group: 'Business' },
  { key: 'transporter', label: '3PL / Transporter', field: 'transporter', group: 'Business' },
  { key: 'tptCat', label: 'Transport category', field: 'tptCat', group: 'Business' },
  { key: 'org', label: 'Organised / unorganised', field: 'org', group: 'Business' },
  { key: 'origin', label: 'Origin', field: 'srcCity', group: 'Geography & lane' },
  { key: 'dstZone', label: 'Destination zone', field: 'dstZone', group: 'Geography & lane' },
  { key: 'dstState', label: 'Destination state', field: 'dstState', group: 'Geography & lane' },
  { key: 'dstCity', label: 'Destination city', field: 'dstCity', group: 'Geography & lane' },
  { key: 'lane', label: 'Lane', field: 'lane', group: 'Geography & lane' },
  { key: 'vehicle', label: 'Vehicle type', field: 'vehicle', group: 'Geography & lane' },
  { key: 'status', label: 'Shipment status', field: 'status', group: 'Performance & POD' },
  { key: 'onTime', label: 'On-time result', field: 'onTimeResult', group: 'Performance & POD' },
  { key: 'etaAdh', label: 'ETA adherence', field: 'etaRes', group: 'Performance & POD', order: ADH_ORDER },
  { key: 'cpdAdh', label: 'CPD adherence', field: 'cpdRes', group: 'Performance & POD', order: ADH_ORDER },
  { key: 'eddAdh', label: 'EDD adherence', field: 'eddRes', group: 'Performance & POD', order: ADH_ORDER },
  { key: 'exception', label: 'Exception reason', field: 'exception', group: 'Performance & POD' },
  { key: 'remark', label: 'Delay remark', field: 'remarkCat', group: 'Performance & POD' },
  { key: 'pod', label: 'POD status', field: 'pod', group: 'Performance & POD' },
  { key: 'podBucket', label: 'POD ageing bucket', field: 'podBucket', group: 'Performance & POD', order: POD_BUCKETS },
];
export const FILTER_BY_KEY = Object.fromEntries(FILTER_DEFS.map((d) => [d.key, d]));
const DATE_KEYS = ['year', 'quarter', 'month'];

const emptyFilters = () => Object.fromEntries(FILTER_DEFS.map((d) => [d.key, []]));

// Default load window: the current year to date (1 Jan to today).
const defaultRange = () => ({
  from: moment().startOf('year').toDate(),
  to: moment().endOf('day').toDate(),
});

let state = {
  status: 'idle', error: null, range: defaultRange(), rows: [], dq: null, refreshedAt: null,
  filters: emptyFilters(), search: '',
  severity: SEVERITY_DEFAULTS, co2FullYear: false, filtersOpen: true, explorerSort: null,
  co2Cfg: loadCo2Cfg(),
};
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
const set = (patch) => { state = { ...state, ...patch }; emit(); };

export function useAm() {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => state);
}

// ---- filtering ----

function rowMatches(r, filters, search, exceptKey, skipDates) {
  for (let i = 0; i < FILTER_DEFS.length; i += 1) {
    const d = FILTER_DEFS[i];
    if (d.key === exceptKey || (skipDates && DATE_KEYS.includes(d.key))) continue;
    const sel = filters[d.key];
    if (sel.length && !sel.includes(r[d.field])) return false;
  }
  if (search) {
    const q = search;
    if (!(r.lr.toLowerCase().includes(q) || r.inv.toLowerCase().includes(q)
      || r.veh.toLowerCase().includes(q) || (r.tripId && r.tripId.toLowerCase().includes(q)))) return false;
  }
  return true;
}

export function filterRows(s, { exceptKey = null, skipDates = false } = {}) {
  const q = s.search.trim().toLowerCase();
  return s.rows.filter((r) => rowMatches(r, s.filters, q, exceptKey, skipDates));
}

let cache = null;
/** Rows under every active filter (memoised per state). */
export function useFilteredRows(s) {
  if (!cache || cache.rows0 !== s.rows || cache.filters !== s.filters || cache.search !== s.search) {
    cache = { rows0: s.rows, filters: s.filters, search: s.search, rows: filterRows(s) };
  }
  return cache.rows;
}

/** Same non-date filters, date filters dropped - for the previous-month comparison. */
export function rowsIgnoringDates(s) {
  return filterRows(s, { skipDates: true });
}

/** Options with counts for one filter, under every other active filter. */
export function facetOptions(s, key) {
  const def = FILTER_BY_KEY[key];
  const counts = new Map();
  filterRows(s, { exceptKey: key }).forEach((r) => {
    const v = r[def.field];
    if (v === null || v === undefined) return;
    counts.set(v, (counts.get(v) || 0) + 1);
  });
  s.filters[key].forEach((v) => { if (!counts.has(v)) counts.set(v, 0); });
  let opts = [...counts.entries()].map(([value, count]) => ({ value, count, label: def.format ? def.format(value) : value }));
  if (def.order) opts.sort((a, b) => def.order.indexOf(a.value) - def.order.indexOf(b.value));
  else if (DATE_KEYS.includes(key)) opts.sort((a, b) => String(a.value).localeCompare(String(b.value)));
  else opts.sort((a, b) => b.count - a.count);
  return opts;
}

// ---- actions ----

export const amActions = {
  setFilter(key, values) { set({ filters: { ...state.filters, [key]: values } }); },
  /** Click-to-filter: selects only this value; clicking the same value again clears it. */
  toggle(key, value) {
    const cur = state.filters[key];
    const next = cur.length === 1 && cur[0] === value ? [] : [value];
    amActions.setFilter(key, next);
  },
  /** Select a set of values; clicking the same set again clears it. */
  toggleSet(key, values) {
    const cur = state.filters[key];
    const same = cur.length === values.length && values.every((v) => cur.includes(v));
    amActions.setFilter(key, same ? [] : [...values]);
  },
  /** Apply several filters at once (drill-through). */
  apply(patch) { set({ filters: { ...state.filters, ...patch } }); },
  removeValue(key, value) { amActions.setFilter(key, state.filters[key].filter((v) => v !== value)); },
  setSearch(search) { set({ search }); },
  reset() { set({ filters: emptyFilters(), search: '' }); },
  setSeverity(severity) { set({ severity }); },
  setCo2FullYear(co2FullYear) { set({ co2FullYear }); },
  /** Change CO2 calculation settings and recalculate every row's distance and CO2. */
  setCo2Cfg(co2Cfg) {
    saveCo2Cfg(co2Cfg);
    applyCo2Settings(state.rows, co2Cfg);
    set({ co2Cfg, rows: [...state.rows] });
  },
  resetCo2Cfg() {
    try { window.localStorage.removeItem(CO2_CFG_KEY); } catch (e) { /* ignore */ }
    const co2Cfg = cloneCfg(CO2_DEFAULTS);
    applyCo2Settings(state.rows, co2Cfg);
    set({ co2Cfg, rows: [...state.rows] });
  },
  setFiltersOpen(filtersOpen) { state = { ...state, filtersOpen }; emit(); },
  /** One-shot sort request for the explorer (e.g. from a KPI card). */
  setExplorerSort(explorerSort) { state = { ...state, explorerSort }; emit(); },
  /**
   * Loads the dispatch window from the lean, cached columnar endpoint.
   * refresh: true bypasses the server cache (the Refresh button).
   */
  load(range = state.range, { refresh = false } = {}) {
    set({ status: 'loading', error: null, range, progress: null });
    const started = Date.now();
    const payload = {
      from: moment(range.from).startOf('day').toISOString(),
      to: moment(range.to).endOf('day').toISOString(),
      refresh,
    };
    return redirectURL.post('consignments/autometricsDashboardData', payload, {
      onDownloadProgress: (e) => {
        if (e && e.loaded) state = { ...state, progress: { mb: e.loaded / 1048576, secs: (Date.now() - started) / 1000 } };
        emit();
      },
    })
      .then((resp) => {
        const d = resp.data || {};
        const fields = Array.isArray(d.fields) ? d.fields : [];
        const raw = (Array.isArray(d.rows) ? d.rows : []).map((cells) => {
          const o = {};
          for (let i = 0; i < fields.length; i += 1) if (cells[i] !== null) o[fields[i]] = cells[i];
          return o;
        });
        const { rows, dq } = prepareRows(raw, { fromDay: dayOf(range.from), toDay: dayOf(range.to) });
        applyCo2Settings(rows, state.co2Cfg);
        set({
          status: 'ready', rows, dq, refreshedAt: new Date(), progress: null, lanes: d.lanes || null,
          loadInfo: { secs: (Date.now() - started) / 1000, cache: resp.headers && resp.headers['x-autometrics-cache'] },
        });
      })
      .catch((err) => {
        console.error('Autometrics dashboard load failed', err);
        set({ status: 'error', progress: null, error: 'Could not load shipment data. Check the connection and try again.' });
      });
  },
  ensureLoaded() { if (state.status === 'idle') amActions.load(); },
};

export function activeFilterCount(s) {
  return FILTER_DEFS.reduce((n, d) => n + s.filters[d.key].length, 0) + (s.search.trim() ? 1 : 0);
}
